import { testMediaConnectionBitrate, MediaConnectionBitrateTest } from '@twilio/rtc-diagnostics';
import { Device, Call, PreflightTest, RTCSample } from '@twilio/voice-sdk';
import { getLogger } from 'loglevel';
import { makeNoCorsFetchrequest, regionalizeIceUrls } from '../../utils';
import { APP_NAME, LOG_LEVEL } from '../../constants';
import { Edge } from '../../types';
import { DNSLookupTestResults, PromiseState, Region, DNSLookUpTest } from './Tests.model';
import { DNSUrls } from './Tests.constants';

const log = getLogger(APP_NAME);

getLogger(Device.packageName).setLevel(LOG_LEVEL, false);

const preflightOptions: PreflightTest.Options = {
  logLevel: 'debug',
  signalingTimeoutMs: 10000,
  fakeMicInput: true,
};

export const BITRATE_TEST_DURATION = 15000;

export function preflightTestRunner(
  edge: Edge,
  token: string,
  iceServers: RTCIceServer[],
  codecPreferences: Call.Codec[]
) {
  const updatedIceServers = regionalizeIceUrls(edge, iceServers);

  return new Promise<PreflightTest.Report>((resolve, reject) => {
    const preflightTest = Device.runPreflight(token, {
      ...preflightOptions,
      edge: edge,
      iceServers: updatedIceServers,
      codecPreferences,
    });
    let hasConnected = false;
    let latestSample: RTCSample;

    preflightTest.on(PreflightTest.Events.Completed, (report: PreflightTest.Report) => {
      log.debug('Preflight Test - report: ', report);
      resolve(report);
    });

    preflightTest.on(PreflightTest.Events.Connected, () => {
      log.debug('Preflight Test - connected');
      hasConnected = true;
    });

    preflightTest.on(PreflightTest.Events.Sample, (sample: RTCSample) => {
      latestSample = sample;
    });

    preflightTest.on(PreflightTest.Events.Failed, (error) => {
      log.debug('Preflight Test - failed: ', error);
      error.hasConnected = hasConnected;
      error.latestSample = latestSample;
      reject(error);
    });

    preflightTest.on(PreflightTest.Events.Warning, (warningName, warningData) => {
      log.debug('Preflight Test - warning: ', warningName, warningData);
    });
  });
}

export function bitrateTestRunner(edge: Edge, iceServers: MediaConnectionBitrateTest.Options['iceServers']) {
  const updatedIceServers = regionalizeIceUrls(edge, iceServers);

  return new Promise<MediaConnectionBitrateTest.Report>((resolve, reject) => {
    const bitrateTest = testMediaConnectionBitrate({ iceServers: updatedIceServers });

    bitrateTest.on(MediaConnectionBitrateTest.Events.Bitrate, (bitrate) => {
      log.debug('Bitrate Test - bitrate: ', bitrate);
    });

    bitrateTest.on(MediaConnectionBitrateTest.Events.Error, (error) => {
      log.debug('Bitrate Test - error: ', error);
      reject(error);
    });

    bitrateTest.on(MediaConnectionBitrateTest.Events.End, (report) => {
      log.debug('Bitrate Test - end: ', report);
      resolve(report);
    });

    setTimeout(() => {
      bitrateTest.stop();
    }, BITRATE_TEST_DURATION);
  });
}

/**
 * DNS lookup Test
 */
export async function dnsLookupTestRunner(region: Region): Promise<DNSLookupTestResults> {
  const urls = DNSUrls[region] || DNSUrls[Region.US];

  const allPromises: Array<Promise<any>> = urls.map((domainUrl) => makeNoCorsFetchrequest(domainUrl));

  const consolidatedResults: DNSLookupTestResults = {
    totalDomains: urls.length,
    domainsReachable: 0,
    individualTestResults: [],
  };

  try {
    const results = await Promise.allSettled(allPromises);
    let totalTestsPassed = 0;
    const individualDomainTestResults: Array<DNSLookUpTest> = results.map(({ status }, index) => {
      if (status === PromiseState.FULFILLED) {
        console.log('Domain Reachable', urls[index]);
        totalTestsPassed++;
        return {
          url: urls[index],
          reachable: true,
        };
      } else {
        console.log('Domain Not Reachable', urls[index]);
        return {
          url: urls[index],
          reachable: false,
        };
      }
    });

    consolidatedResults.domainsReachable = totalTestsPassed;
    consolidatedResults.individualTestResults = individualDomainTestResults;

    console.log(`${consolidatedResults.totalDomains} of ${consolidatedResults.domainsReachable} Passed`);
  } catch (err) {
    console.log('Error performing DNS Lookup Test');
  } finally {
    return consolidatedResults;
  }
}
